import { FC, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux";
import { fetchBulkCreateWhitelistEntries, fetchBulkCreateWhitelistEntriesFromCSV, fetchWhitelistedPhones, selectBulkImportResult, clearBulkImportResult } from "../../model";
import notify from "@/shared/lib/notify";
import { getErrorMessage } from "@/shared/consts/error-codes";
import { FormInput } from "@/shared/ui/form-input";
import { useForm, SubmitHandler } from "react-hook-form";

interface BulkImportFormData {
    phones: string;
}

export const BulkImport: FC = () => {
    const dispatch = useAppDispatch();
    const bulkImportResult = useAppSelector(selectBulkImportResult);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors }
    } = useForm<BulkImportFormData>();

    const onSubmit: SubmitHandler<BulkImportFormData> = async (data) => {
        setIsSubmitting(true);
        try {
            const phones = data.phones
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0);
            
            const result = await dispatch(fetchBulkCreateWhitelistEntries({ phones })).unwrap();
            notify.success(`Успешно добавлено: ${result.created}, пропущено: ${result.skipped}`);
            reset();
            dispatch(fetchWhitelistedPhones());
        } catch (error: any) {
            notify.error(getErrorMessage(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.match(/\.(csv|txt)$/i)) {
            notify.error('Поддерживаются только файлы .csv и .txt');
            return;
        }

        setIsUploading(true);
        try {
            const result = await dispatch(fetchBulkCreateWhitelistEntriesFromCSV(file)).unwrap();
            notify.success(`Успешно добавлено: ${result.created}, пропущено: ${result.skipped}`);
            dispatch(fetchWhitelistedPhones());
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error: any) {
            notify.error(getErrorMessage(error));
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="box">
            <div className="box_header !mb-2 font-mediumttf">
                Импорт
            </div>
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-3">
                    <div className="text-[13px] font-semibold text-color-secondary">
                        Введите телефоны (по одному на строку):
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                        <FormInput
                            register={register as any}
                            id="phones"
                            label="Телефоны"
                            type="textarea"
                            required
                            placeholder="+79999999999"
                            rows={6}
                            error={errors.phones}
                        />
                        <button
                            className="form_handle_button"
                            disabled={isSubmitting}
                            type="submit"
                        >
                            Импортировать
                        </button>
                    </form>
                </div>
                <div className="w-full bg-[#B9C9C6] h-[1px]" />
                <div className="flex flex-col gap-3">
                    <div className="text-[13px] font-semibold text-color-secondary">
                        Или загрузите CSV/TXT файл:
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                        className="hidden"
                        id="csv-upload"
                    />
                    <label
                        htmlFor="csv-upload"
                        className={`form_handle_button flex items-center justify-center cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isUploading ? 'Загрузка...' : 'Выбрать файл'}
                    </label>
                </div>
            </div>
        </div>
    );
};

